import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ActionIcon, Alert, Anchor, Badge, Button, Center, CloseButton, Code, Group, Loader, Pagination,
  Paper, Select, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import {
  IconAlertCircle, IconAlertTriangle, IconChevronDown, IconChevronRight, IconInfoCircle,
  IconRefresh, IconSearch, IconX,
} from '@tabler/icons-react'
import {
  PageTitle, SectionAction, SectionCard, StatusBadge, EmptyState, MetaList, HairlineDivider,
} from '@lighthouse/design'
import {
  connectionLifetime, msLabel, outcomeGloss, outcomeLabel, outcomeRole, reachesPastRetention,
  retentionLabel, retryDepth, rowsLandedLabel, windowLabel,
} from '../components/connections/connectionFormat'
import { useConnections } from '../hooks/useOverview'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../utils/format'
import { errorTextView } from '../utils/haltDetail'
import type { ConnectionQuery, EtlConnection, EtlConnectionOutcome } from '../types/observatory'

/** Server-clamped to 200. Fifty matches the audit log, the app's other paged log. */
const PAGE_SIZE = 50

const OUTCOMES: EtlConnectionOutcome[] = ['running', 'succeeded', 'failed', 'abandoned']

interface FilterState {
  entityName: string
  module: string
  outcome: EtlConnectionOutcome | null
  /**
   * `YYYY-MM-DD`, because that is what Mantine 8's `DateInput` actually emits.
   *
   * Its `onChange` is typed `(value: DateStringValue | null) => void` — a
   * STRING. Holding these as `Date` and casting the callback argument, which is
   * what the audit log did, type-checks and then throws
   * `value.toISOString is not a function` the moment a date filter is applied,
   * because the cast is a lie the compiler is not allowed to question.
   */
  from: string | null
  to: string | null
}

const EMPTY_FILTERS: FilterState = {
  entityName: '',
  module: '',
  outcome: null,
  from: null,
  to: null,
}

/**
 * Connections — every connection the ETL has opened to a source, for 30 days.
 *
 * `GET api/observatory/connections` has been live and consumed by nothing since
 * the attempt table shipped. This page is that endpoint and no more: no
 * controller, no DTO, no migration, nothing recomputed.
 *
 * **One row is one connection**, which is the whole reason the backing table
 * exists. `Etl.EtlRunChunk` was always 1:1 with an Oracle connection in the
 * moment, but its id is the landing stamp a retry deletes by, so it has to
 * survive the retry unchanged — and a chunk retried three times before
 * succeeding was one row saying "succeeded", with the three failures an operator
 * most wants overwritten. `Etl.EtlRunChunkAttempt` is where they now live.
 *
 * **This is not `/sources` with more columns.** That page is one row per source,
 * overwritten every sixty seconds, answering "can we reach it now".  This one
 * overwrites nothing and answers "what happened". Current state versus history;
 * the two are linked in both directions rather than merged — the header's
 * "← Sources" here, and a "Connection history" pivot under every connector there.
 *
 * **And to the run each connection belonged to.** A connection's entity run opens
 * in the ETL Explorer, and the run filter (`?runId=`, which the Explorer links in
 * with) says which run it is, clears in place, and links back to that run. The
 * id is the same on all three surfaces — `api/observatory/runs/{runId}` and this
 * endpoint's `runId` both take the ENTITY run, never the orchestrator pass.
 *
 * Four decisions shape it:
 *
 *  1. **A table, and the reason is scale, not taste.** `/sources` and `/workers`
 *     use a card per row because they have one to a handful. Thirty days here is
 *     up to ~6,300 rows. Cards would be a scroll, not a page.
 *  2. **Every filter is applied server-side, and paging is real.** A page is at
 *     most 200 of ~6,300 rows, so anything computed across "the rows" would be
 *     computed across a window and read as a total. There is no client-side
 *     filter, sort or aggregate anywhere on this page.
 *  3. **No success rate, and no rate of any kind.** It is the obvious headline
 *     and it is unavailable honestly: the denominator would be one page, not the
 *     ledger, and on the ordinary day the ledger is EMPTY — every ETL job is
 *     manual-trigger — which divides to `0%` and reads as total failure. The
 *     served `totalCount` is rendered as a count.
 *  4. **`closedAt` is never read as a success flag.** Null means this attempt
 *     never completed a landing, NOT that it failed; an attempt that landed and
 *     then failed in the transform keeps a real close instant, and on the two
 *     ~100M-row entities that is the common shape. It is paired with `landingMs`
 *     everywhere it appears — see `connectionLifetime`.
 */
export function ConnectionsPage() {
  const navigate = useNavigate()

  // `runId` arrives by URL rather than by form, because it is a pivot from
  // another surface (`api/observatory/runs/{runId}` takes the same id) and not
  // something anyone types. Kept in the query string so the pivoted view is a
  // link somebody can send.
  const [searchParams, setSearchParams] = useSearchParams()
  const runId = searchParams.get('runId')

  // Draft is what the form holds; applied is what the query runs on. Separated
  // so typing an entity name does not fire a request per keystroke against a
  // table this size.
  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)

  const query = toQuery(applied, page, runId)
  const { data, isLoading, isError, error, isFetching, refetch } = useConnections(query)

  const applyFilters = () => {
    setPage(1)
    setApplied(draft)
  }

  // Removes the run pivot and nothing else. Page 1 because the page number was a
  // position in the RUN's connections, and carried over it would land a reader on
  // an arbitrary page of the whole ledger.
  const clearRun = () => {
    setPage(1)
    const next = new URLSearchParams(searchParams)
    next.delete('runId')
    setSearchParams(next, { replace: true })
  }

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    setPage(1)
    // The run pivot is a filter too, and "Clear" that left it in place would be
    // the page disagreeing with its own button.
    if (runId) clearRun()
  }

  const rows = data?.data ?? []
  const hasFilters = runId != null || isFiltered(applied)

  return (
    <Stack gap="lg" pb="xl">
      <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
        <PageTitle subtitle="Every connection the ETL opened to a source, when it closed, and what it carried — 30 days">
          Connections
        </PageTitle>
        {/*
          Back to current state. onClick + navigate, never `href`: SectionAction's
          href renders a bare <a>, which inside BrowserRouter is a full page reload.
        */}
        <div style={{ paddingTop: 10, flexShrink: 0 }}>
          <SectionAction onClick={() => navigate('/sources')}>← Sources</SectionAction>
        </div>
      </Group>

      {runId && (
        <RunPivot
          runId={runId}
          onClear={clearRun}
          onOpenRun={() => navigate(explorerPath(runId))}
        />
      )}

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <Group align="flex-end" gap="sm">
          <TextInput
            label="Entity"
            placeholder="e.g. SupplierInvoice"
            value={draft.entityName}
            onChange={(e) => setDraft({ ...draft, entityName: e.currentTarget.value })}
          />
          <TextInput
            label="Module"
            placeholder="e.g. Payables"
            value={draft.module}
            onChange={(e) => setDraft({ ...draft, module: e.currentTarget.value })}
          />
          <Select
            label="Outcome"
            placeholder="Any"
            clearable
            data={OUTCOMES.map((value) => ({ value, label: outcomeLabel(value) }))}
            value={draft.outcome}
            onChange={(value) => setDraft({ ...draft, outcome: asOutcome(value) })}
            w={150}
          />
          {/*
            "Started", matching the column and matching what the endpoint actually
            filters on -- `StartedAt`, the attempt's own clock. Labelling these
            "Opened" would promise a filter on the connection's open instant,
            which is a different and later moment, and is null on every attempt
            that never completed a landing. A date filter that silently excluded
            exactly the failed connections would be the worst possible one here.
          */}
          <DateInput
            label="Started from"
            placeholder="Start date"
            clearable
            value={draft.from}
            onChange={(value) => setDraft({ ...draft, from: value })}
            w={150}
          />
          <DateInput
            label="Started to"
            placeholder="End date"
            clearable
            value={draft.to}
            onChange={(value) => setDraft({ ...draft, to: value })}
            w={150}
          />
          <Button leftSection={<IconSearch size={16} />} onClick={applyFilters}>
            Search
          </Button>
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={clearFilters}>
            Clear
          </Button>
          {/*
            An explicit refresh, because this query deliberately does not poll:
            the ledger is append-only and every ETL job is manual-trigger, so
            there is no unattended clock for a poll to keep up with. The button
            is what replaces it, and it says when it last looked.
          */}
          <Tooltip label="Re-read the ledger">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => void refetch()}
              loading={isFetching}
              ml="auto"
            >
              Refresh
            </Button>
          </Tooltip>
        </Group>
      </Paper>

      {/*
        `|| !data` is load-bearing and was missing for one revision, which is how
        this page briefly claimed "No connections recorded yet" while the request
        was in fact 500-ing.

        React Query retries a failed query, and BETWEEN retries the flags are
        `isError: false`, `isLoading: false`, `data: undefined` -- a combination
        that reads as "loaded, and empty" on every test a page normally makes. An
        unreached endpoint then renders as an empty ledger, which on this surface
        is not a cosmetic slip: an empty ledger is the ORDINARY state here, so the
        lie is indistinguishable from the truth and nobody would ever catch it.
        The absence of data is not the same finding as a measured zero.
      */}
      {isError ? (
        <LedgerError error={error} runId={runId} />
      ) : isLoading || !data ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <>
          <LedgerContext
            totalCount={data.totalCount}
            oldestRetainedAt={data.oldestRetainedAt}
            from={query.from ?? null}
            hasFilters={hasFilters}
          />

          <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            {rows.length === 0 ? (
              <EmptyLedger hasFilters={hasFilters} />
            ) : (
              <>
                {/*
                  Eight columns, two of them full timestamps. The page must never
                  scroll sideways as a whole -- the filter bar and the retention
                  line above would drift off with it -- so the overflow is the
                  table's own rather than the body's.
                */}
                <Table.ScrollContainer minWidth={900}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      {/*
                        "Attempt started", not "Opened" -- this column renders
                        `startedAt`, and the attempt starts one landing-table
                        delete BEFORE the connection opens. On a first attempt
                        that delete removes nothing and the gap is negligible; on
                        a retry it removes the previous attempt's partial rows and
                        doc 15 records it as material. `openedAt` is a separate
                        field and lives in the detail panel, where it can sit
                        beside the close instant it is paired with.
                      */}
                      <Table.Th>Attempt started</Table.Th>
                      <Table.Th>Entity</Table.Th>
                      <Table.Th>Window</Table.Th>
                      <Table.Th>Slice</Table.Th>
                      {/*
                        "Held", not "Duration": this is how long the SOURCE
                        connection was open, which is not how long the attempt
                        took. The attempt also transforms, after the connection
                        has closed, and on the two largest entities that is the
                        larger half.
                      */}
                      <Table.Th>Held</Table.Th>
                      <Table.Th ta="right">Rows landed</Table.Th>
                      <Table.Th>Outcome</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((connection) => (
                      <ConnectionRow
                        key={connection.attemptId}
                        connection={connection}
                        isExpanded={expanded === connection.attemptId}
                        onToggle={() => setExpanded(
                          expanded === connection.attemptId ? null : connection.attemptId,
                        )}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
                </Table.ScrollContainer>

                <Group justify="space-between" mt="md">
                  <Text size="sm" c="dimmed">
                    Page {data.pageNumber} of {data.totalPages}
                    {' · '}
                    {exactNumber(data.totalCount)} connections
                    {hasFilters ? ' matching these filters' : ' on file'}
                  </Text>
                  {data.totalPages > 1 && (
                    <Pagination value={page} total={data.totalPages} onChange={setPage} />
                  )}
                </Group>
              </>
            )}
          </Paper>
        </>
      )}
    </Stack>
  )
}

/**
 * One connection, plus its detail when opened.
 *
 * Two `<tr>`s rather than a modal: the error text is the highest-value field on
 * the row and it is up to 2,000 characters, so it needs the full table width,
 * and a modal would also hide the neighbouring rows — which for a retried chunk
 * are the earlier attempts at the same slice, the single most useful comparison
 * this table offers.
 */
function ConnectionRow({
  connection, isExpanded, onToggle,
}: {
  connection: EtlConnection
  isExpanded: boolean
  onToggle: () => void
}) {
  const role = outcomeRole(connection.outcome)
  const retry = retryDepth(connection.attemptNumber)

  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <Table.Td>
          {/*
            The chevron carries its own handler rather than relying on the row's.
            A focusable control that does nothing on Enter is a keyboard dead end,
            and the row itself cannot be the keyboard affordance -- a <tr> takes no
            focus. stopPropagation because otherwise a click here toggles twice.
          */}
          <ActionIcon
            variant="subtle"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} detail for attempt ${connection.attemptNumber} of ${connection.entityName} slice ${connection.chunkIndex}`}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
          >
            {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          {/*
            startedAt, not openedAt. The attempt opens one landing-table delete
            before the connection does, and startedAt is the one that is never
            null -- ordering a column on a field that can be absent puts the rows
            that matter most at the bottom.
          */}
          {whenLabel(connection.startedAt)}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{connection.entityName}</Text>
          <Text size="xs" c="dimmed">{connection.module ?? 'No module recorded'}</Text>
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {windowLabel(connection.windowStart, connection.windowEnd)}
          </Text>
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm">#{connection.chunkIndex}</Text>
          {retry && <Text size="xs" c="orange">{retry}</Text>}
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>{connectionLifetime(connection)}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {rowsLandedLabel(connection.rowsLanded)}
        </Table.Td>
        <Table.Td>
          {/*
            `running` has no status role and gets no badge -- it is not good news
            and not bad news, and the badge vocabulary has no member for the
            absence of news. Same reason `status` is nullable on source health.
          */}
          {role
            ? <StatusBadge status={role} label={outcomeLabel(connection.outcome)} />
            : <Text size="sm" c="dimmed">{outcomeLabel(connection.outcome)}</Text>}
        </Table.Td>
      </Table.Tr>

      {isExpanded && (
        <Table.Tr>
          <Table.Td colSpan={8} style={{ backgroundColor: 'var(--color-bg-inset, transparent)' }}>
            <ConnectionDetail connection={connection} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

/**
 * Everything the row could not hold, at full width.
 *
 * Exported for its test only: the detail renders when a row is expanded, and an
 * expansion is a click, which a render to string cannot make.
 */
export function ConnectionDetail({ connection }: { connection: EtlConnection }) {
  const navigate = useNavigate()
  const gloss = outcomeGloss(connection.outcome)

  return (
    <Stack gap="md" py="sm">
      {gloss && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title={outcomeLabel(connection.outcome)}>
          <Text size="sm">{gloss}</Text>
        </Alert>
      )}

      <MetaList
        items={[
          { label: 'Connector', value: connection.connectorId, mono: true },
          { label: 'Source system', value: connection.sourceSystem },
          // The RUN's worker, not an observed property of the connection --
          // nothing records which host a given connection was opened from. With
          // connectorId it is the composite key /sources is keyed on, which is
          // what makes "this failed, what does that host think of Oracle now" a
          // question a reader can follow.
          { label: 'Host that ran the pass', value: connection.hostName ?? NOT_MEASURED, mono: true },
          { label: 'Run key', value: connection.runKey, mono: true },
          {
            label: 'Entity run',
            // The ENTITY run, which is what the Explorer's route takes — not the
            // orchestrator pass the run key above names. A button styled as a
            // link, not an <a href>, for the same full-reload reason as the
            // header's action; `inherit` keeps the mono face the row asks for.
            value: (
              <Anchor
                component="button"
                type="button"
                inherit
                ta="left"
                aria-label={`Open entity run ${connection.entityRunId} in the ETL Explorer`}
                onClick={() => navigate(explorerPath(connection.entityRunId))}
              >
                {connection.entityRunId}
              </Anchor>
            ),
            mono: true,
          },
          // Repeated across every attempt at the same slice, deliberately: two
          // rows sharing it are two connections that tried the same window.
          { label: 'Chunk', value: connection.chunkId, mono: true },
          { label: 'Attempt', value: `${connection.attemptNumber} of this chunk` },
        ]}
      />

      <HairlineDivider />

      <Stack gap="xs">
        <Text size="sm" fw={500}>The connection</Text>
        <MetaList
          items={[
            { label: 'Attempt started', value: whenLabel(connection.startedAt) },
            { label: 'Connection opened', value: whenLabel(connection.openedAt) },
            { label: 'Connection closed', value: whenLabel(connection.closedAt) },
            { label: 'Held for', value: connectionLifetime(connection) },
            { label: 'Attempt ended', value: whenLabel(connection.completedAt) },
          ]}
        />
        {connection.landingMs != null && connection.closedAt == null && (
          <Text size="xs" c="dimmed">
            A measured duration with no close instant means the connection was held for that long and
            then lost, rather than closed. It is the only place a death and a close can be told apart —
            which is why this pairs with the duration and never with the outcome.
          </Text>
        )}
      </Stack>

      <HairlineDivider />

      <Stack gap="xs">
        <Text size="sm" fw={500}>What it carried</Text>
        <MetaList
          items={[
            { label: 'Rows landed by this connection', value: rowsLandedLabel(connection.rowsLanded) },
            // Never labelled possessively. The transform procedure takes no
            // chunk id and drains the landing table wholesale, so this
            // legitimately includes rows landed by an earlier chunk or run.
            {
              label: 'Rows the transform drained while this ran',
              value: exactNumber(connection.rowsTransformed),
            },
            { label: 'Time holding the connection', value: msLabel(connection.landingMs) },
            { label: 'Time in the transform', value: msLabel(connection.transformMs) },
          ]}
        />
        <Text size="xs" c="dimmed">
          The two row counts are not comparable and the two durations are never summed. Rows landed is
          this connection&rsquo;s own figure; rows drained is whatever the transform swept while this
          attempt ran, which can include another connection&rsquo;s work. The durations answer whether
          the source was slow or the warehouse was, and one combined figure would answer neither.
        </Text>
      </Stack>

      <ConnectionError connection={connection} />
    </Stack>
  )
}

/**
 * What ended the attempt. Withheld from a non-administrator when the attempt's run halted, or the text copies a
 * halt's detail (D8) — then it says DETAIL_ADMIN_ONLY, dimmed, rather than vanishing as if nothing ended it. The
 * server's `errorWithheld` decides, never the caller's role.
 */
function ConnectionError({ connection }: { connection: EtlConnection }) {
  const view = errorTextView(connection.error, connection.errorWithheld)
  if (view.kind === 'none') return null

  return (
    <>
      <HairlineDivider />
      <Stack gap="xs">
        <Text size="sm" fw={500}>What ended it</Text>
        {view.kind === 'withheld' ? (
          <Text size="sm" c="dimmed" data-state="detail-withheld">{DETAIL_ADMIN_ONLY}</Text>
        ) : (
          <>
            <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {view.text}
            </Code>
            <Text size="xs" c="dimmed">
              Truncated to 2,000 characters when it was written, and never cleared by a later attempt —
              this list is the only place an earlier attempt&rsquo;s failure survives.
            </Text>
          </>
        )}
      </Stack>
    </>
  )
}

/**
 * What the list cannot say about itself: how far back it reaches.
 *
 * Rendered above the table in every state including the empty one, because it is
 * the difference between "nothing failed before this date" and "nothing before
 * this date is still on file". A reader who cannot tell those apart reads a
 * retention boundary as a clean bill of health.
 */
function LedgerContext({
  totalCount, oldestRetainedAt, from, hasFilters,
}: {
  totalCount: number
  oldestRetainedAt: string | null
  from: string | null
  hasFilters: boolean
}) {
  const overreaching = reachesPastRetention(from, oldestRetainedAt)

  return (
    <SectionCard
      title="What this record covers"
      subtitle={
        hasFilters
          ? `${exactNumber(totalCount)} connections match the current filters`
          : `${exactNumber(totalCount)} connections on file`
      }
    >
      <Stack gap="sm">
        {/*
          No success rate, and no rate of any kind. The denominator available
          here is one page of at most 200 out of ~6,300 rows, so any percentage
          would be computed over a window and read as a total -- and on the
          ordinary day the ledger is empty, which divides to 0% and reads as
          total failure when the truth is that nobody has run a load. The count
          is served; it is rendered as a count.
        */}
        <Text size="sm">{retentionLabel(oldestRetainedAt)}</Text>

        {overreaching && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="This filter reaches past the record">
            <Text size="sm">
              The date asked for is older than the oldest connection still on file, so these results are
              bounded by retention rather than by the filter. An empty or short result here is not
              evidence that nothing happened.
            </Text>
          </Alert>
        )}

        <HairlineDivider />

        {/*
          Two standing limits, shown always. Both are cases where the page would
          otherwise be read as more complete than it is, and doc 15 §10 records
          them as things the page should say rather than things a reader should
          find out.
        */}
        <Text size="xs" c="dimmed">
          <strong>These are pool leases, not sockets.</strong> ODP.NET pools connections, so
          &ldquo;opened&rdquo; is a checkout from the pool and &ldquo;closed&rdquo; is a return to it.
          The physical connect happens once and is invisible to application code, so a fast open here
          does not mean the network was fast.
        </Text>
        <Text size="xs" c="dimmed">
          <strong>The ledger undercounts.</strong> It records the chunked extract path, which is all 56
          warehouse entities — but the GL code-combination lookup reads its whole table once per
          orchestrator pass on a different code path, and that connection appears in no telemetry table
          at all. Connections here is a floor, not a total.
        </Text>
      </Stack>
    </SectionCard>
  )
}

/**
 * No rows — and on this page that is usually correct rather than broken.
 *
 * The distinction the copy has to carry: an empty ledger is the NORMAL state of
 * a system whose every ETL job is manual-trigger. Nothing is scheduled, so
 * nothing connects until a person presses Run. A bare "no results" would read as
 * a fault on the day the system is working exactly as configured — the same
 * failure mode the all-dashes state on `/sources` was written against.
 */
function EmptyLedger({ hasFilters }: { hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <EmptyState
        icon={<IconSearch size={28} stroke={1.5} />}
        title="No connections match these filters"
        message={
          'Nothing in the retained window matches. Widen the dates or clear the filters — and note '
          + 'that the ledger only reaches as far back as the line above says it does.'
        }
      />
    )
  }

  return (
    <EmptyState
      icon={<IconInfoCircle size={28} stroke={1.5} />}
      title="No connections recorded yet"
      message={
        'This is the expected state on a system where nothing has run. Every ETL job is '
        + 'manual-trigger — the schedules exist but their cron never fires — so the ledger fills only '
        + 'when someone starts a load from the Scheduler. An empty ledger means no connection has been '
        + 'opened, not that connections are not being recorded.'
      }
    />
  )
}

/**
 * The request failed, which says nothing about whether any source is reachable.
 *
 * A 400 is told apart from the rest, because on this page it has one likely
 * cause and it is not the database: the run pivot arrives by URL, the endpoint
 * binds it as a GUID, and a truncated or hand-edited link is refused before the
 * ledger is read. Blaming a missing migration for that would send a reader to
 * the wrong place entirely. The server's own sentence follows either way.
 */
function LedgerError({ error, runId }: { error: unknown; runId: string | null }) {
  const said = errorSummary(error)

  if (errorKind(error) === 'bad-request') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="The ledger refused these filters"
        message={
          <>
            {'The API rejected the query as malformed, so nothing about the ledger was read and nothing '
            + 'here says whether any connection succeeded.'}
            {runId && (
              ' The likeliest cause is the run id in this page\'s address: the endpoint takes an entity '
              + 'run id, which is a GUID, and a truncated or edited link sends something else. Clearing '
              + 'the run filter above removes it.'
            )}
            {said && <> The request said: {said}</>}
          </>
        }
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not load the connection ledger"
      message={
        <>
          {'The request did not come back. That is a fault between this page and the API and says nothing '
          + 'about whether any connection succeeded. The likeliest cause on a fresh database is migration '
          + 'AddEtlRunChunkAttempts not having been applied, since migrations here are applied by hand and '
          + 'never by the app; but a rejected sign-in and an API that is not running land here '
          + 'identically. The browser console distinguishes them.'}
          {said && <> The request said: {said}</>}
        </>
      }
    />
  )
}

/**
 * The run pivot, as a chip: which run, a way out of it, and a way back to it.
 *
 * The clear control is a real, focusable, labelled button. Mantine's `Pill` was
 * the obvious chip and is the wrong one here: its remove button ships with
 * `tabIndex={-1}` and `aria-hidden`, because it is designed for a pills input
 * where Backspace removes — on its own it would be a clear control no keyboard
 * can reach and no screen reader announces.
 *
 * Clearing removes `runId` only, and keeps whatever else the form holds: every
 * other filter was applying on top of the run and still means what it meant.
 */
function RunPivot({
  runId, onClear, onOpenRun,
}: {
  runId: string
  onClear: () => void
  onOpenRun: () => void
}) {
  return (
    <Group gap="sm" wrap="wrap" align="center">
      <Badge
        size="lg"
        variant="light"
        color="blue"
        radius="sm"
        tt="none"
        maw="100%"
        rightSection={(
          <CloseButton
            size="xs"
            variant="transparent"
            aria-label={`Clear the filter to run ${runId}`}
            onClick={onClear}
          />
        )}
      >
        Connections for run <span style={{ fontFamily: 'var(--font-mono)' }}>{runId}</span>
      </Badge>
      <SectionAction onClick={onOpenRun}>Open this run →</SectionAction>
      <Text size="xs" c="dimmed">
        Only connections this entity run opened. Every other filter still applies on top of it.
      </Text>
    </Group>
  )
}

/**
 * The ETL Explorer's route for one entity run.
 *
 * Encoded although every id the server hands out is a GUID, because the run
 * pivot's id comes from the address bar and is only as well-formed as whoever
 * last edited it — a stray `/` or `?` would otherwise navigate somewhere else.
 */
function explorerPath(entityRunId: string): string {
  return `/etl-explorer/${encodeURIComponent(entityRunId)}`
}

/**
 * Mantine's `string | null`, narrowed by CHECKING rather than by asserting.
 *
 * `value as EtlConnectionOutcome` would be sound today — the `data` prop is built
 * from `OUTCOMES`, so those four strings are the only ones selectable. It is
 * written as a check anyway, because the sibling `DateInput` on this same form
 * carried exactly that kind of assertion about exactly this kind of value, was
 * wrong, type-checked for months and threw at runtime. An unknown value reaching
 * the endpoint is a 400, so the cost of being wrong here is a refused request
 * rather than a filter that quietly does nothing.
 */
function asOutcome(value: string | null): EtlConnectionOutcome | null {
  return OUTCOMES.find((candidate) => candidate === value) ?? null
}

/** Whether anything in the form is actually narrowing the list. */
function isFiltered(filters: FilterState): boolean {
  return filters.entityName.trim() !== ''
    || filters.module.trim() !== ''
    || filters.outcome !== null
    || filters.from !== null
    || filters.to !== null
}

/**
 * The form, as the endpoint's query.
 *
 * `to` is pushed to the end of its day. The endpoint's upper bound is EXCLUSIVE
 * and documented as such, so passing a bare date would exclude that whole day's
 * own connections — a reader who picks today as the end date and sees nothing
 * from today would reasonably conclude nothing ran today. The stored timestamps
 * are `datetimeoffset(0)`, so `23:59:59.999` is strictly above every instant
 * that day can hold and the exclusive bound still admits all of them.
 */
function toQuery(filters: FilterState, page: number, runId: string | null): ConnectionQuery {
  return {
    entityName: filters.entityName.trim() || undefined,
    module: filters.module.trim() || undefined,
    outcome: filters.outcome ?? undefined,
    runId: runId ?? undefined,
    from: dayBoundary(filters.from, '00:00:00.000'),
    to: dayBoundary(filters.to, '23:59:59.999'),
    skip: (page - 1) * PAGE_SIZE,
    top: PAGE_SIZE,
  }
}

/**
 * A `YYYY-MM-DD` picked in the browser, as the absolute instant the API filters on.
 *
 * The time is appended WITHOUT a trailing `Z`, deliberately: `new Date('2026-08-13')`
 * parses as UTC midnight, while `new Date('2026-08-13T00:00:00.000')` parses as local
 * midnight. Someone west of UTC picking "today" with the first form gets a bound in
 * their own yesterday, silently dropping or adding a day's connections at the edge.
 * The picker means the day the reader is looking at.
 */
function dayBoundary(day: string | null, time: string): string | undefined {
  if (!day) return undefined
  const parsed = new Date(`${day}T${time}`)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}
